import React, { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import { Container, Graphics, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { Settings } from "../../../../settings";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  updateDisplayedBalance,
  setHoldAndWinOutroCounterFinished,
} from "../../../../State/actions";
import {
  selectCurrentRound,
  selectCurrency,
  selectIsSpinning,
  selectCounterAnimationStarted,
  selectCriticalAnimationPlaying,
  selectFreeRoundsStatus,
  selectFreeRoundsEnded,
  selectFreeRoundsCompletionPending,
  selectHoldAndWinActive,
  selectHoldAndWinCurrentRound,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectIsHoldAndWinCompletionReload,
  selectShowHoldAndWinIntro,
  selectFeatureState,
  selectTriggerRoundWin,
  selectFreeSpinActive,
  selectFreeSpinSpinning,
} from "../../../../State/selectors";
import {
  selectFreeSpinTotal,
  selectFreeSpinIndex,
} from "../../../../State/Slices/featureGameSettingsSlice";
import {
  selectHoldAndWinOutroCounterFinished,
  selectWinAnimationCounterFinished,
} from "../../../../State/Slices/animationsSlice";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

/**
 * WinDisplay - Win amount display component for regular gameplay
 *
 * Handles two modes:
 * 1. Regular win (single line "Win: X")
 * 2. H&W trigger round win (dual line "Total Win: X" + "Win: Y" after outro counter)
 *
 * Note: Free rounds use FreeRoundsWinDisplay component instead.
 *
 * @param {Object} toolbar - Toolbar reference for positioning
 * @param {string} variant - 'toolbar' (default) or 'reelset' (under reels in GameContainer)
 */
const WinDisplay = ({ toolbar, variant = "toolbar" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentRound = useSelector(selectCurrentRound);
  const currency = useSelector(selectCurrency);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const isAnimatedWin = useSelector(selectIsAnimatedWin);
  const isHoldAndWinCompletionReload = useSelector(
    selectIsHoldAndWinCompletionReload,
  );
  const showHoldAndWinIntro = useSelector(selectShowHoldAndWinIntro);
  const featureState = useSelector(selectFeatureState);
  const triggerRoundWin = useSelector(selectTriggerRoundWin);
  const holdAndWinOutroCounterFinished = useSelector(
    selectHoldAndWinOutroCounterFinished,
  );
  const winAnimationCounterFinished = useSelector(
    selectWinAnimationCounterFinished,
  );
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const freeSpinSpinning = useSelector(selectFreeSpinSpinning);
  const freeSpinTotal = useSelector(selectFreeSpinTotal);
  const freeSpinIndex = useSelector(selectFreeSpinIndex);

  // True when the current spin is a H&W trigger (intro phase) - coins landed but feature hasn't started yet
  const isHoldAndWinTriggerRound = useMemo(
    () =>
      featureState?.type === "holdAndWin" && featureState?.phase === "intro",
    [featureState?.type, featureState?.phase],
  );

  // True when we are in the H&W active phase AND the trigger round had a regular win
  // This is the special dual-line mode
  const isHoldAndWinTriggerWithWin = useMemo(
    () => holdAndWinActive && triggerRoundWin > 0,
    [holdAndWinActive, triggerRoundWin],
  );

  // Latch ref: stays true once dual-line mode is entered, until next spin resets it.
  // This keeps the dual-line display visible during the exit scene transition and
  // in base game after H&W ends, until the player spins again.
  // Both set and clear happen at render-time (not in useEffect) so shouldShowDualLine
  // reacts in the same frame — no one-render delay or stale "Total Win: 0" flash.
  const wasHoldAndWinTriggerWithWinRef = useRef(false);
  if (isHoldAndWinTriggerWithWin) {
    wasHoldAndWinTriggerWithWinRef.current = true;
  } else if (triggerRoundWin === 0) {
    // Clear latch the moment Redux clears triggerRoundWin (happens on next spin).
    // This is the earliest reliable signal that the feature data has been wiped,
    // so shouldShowDualLine collapses to false in the same render frame.
    wasHoldAndWinTriggerWithWinRef.current = false;
  }

  // Latch ref: keeps the single-line "Win: X" visible for H&W trigger rounds that also
  // have an animated win (Big Win popup). The win animation closes before holdAndWinActive
  // becomes true, causing a brief gap where shouldShowWin is false. This latch bridges
  // that gap. It is only armed AFTER winAnimationCounterFinished becomes true (counter done),
  // so it does NOT fire early — it only takes over once the counter has already shown the win.
  const wasHoldAndWinTriggerRoundRef = useRef(false);
  if (
    isHoldAndWinTriggerRound &&
    currentRound?.win &&
    winAnimationCounterFinished
  ) {
    // Arm the latch only once the win animation counter has finished
    wasHoldAndWinTriggerRoundRef.current = true;
  } else if (holdAndWinActive || (isSpinning && !holdAndWinActive)) {
    // Clear once dual-line takes over or on next spin
    wasHoldAndWinTriggerRoundRef.current = false;
  }

  const {
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
    symbolHeight,
    dimensions,
  } = useDimensions();

  // Track previous shouldShowWin state to detect when WinDisplay becomes visible
  const prevShouldShowWinRef = useRef(false);
  const prevShowWinForPulseRef = useRef(false);
  const prevShouldShowDualLineRef = useRef(false);
  const prevShowWinForSyncRef = useRef(false); // separate tracker for FS total sync

  // Ref for Total Win container (for anime.js slide animation in dual-line mode)
  const totalWinContainerRef = useRef(null);
  const slideAnimationRef = useRef(null);

  // Ref for the text content container (for pulse animation on win appear)
  const winTextContainerRef = useRef(null);

  // Ref for free spin total win — buffered display value
  // Only updates when shouldShowWin becomes true, not when Redux updates
  const displayedFreeSpinTotalRef = useRef(0);
  const [displayedFreeSpinTotal, setDisplayedFreeSpinTotal] = React.useState(0);
  const prevFreeSpinTotalRef = useRef(0);
  const freeSpinTotalContainerRef = useRef(null);
  const freeSpinTotalPulseRef = useRef(null);

  // Refs for smooth bar expand/collapse animation
  const outerContainerRef = useRef(null);
  const bgGraphicsRef = useRef(null);
  const expandAnimRef = useRef(null);
  const prevHasFreeSpinTotalRef = useRef(false);

  // Ref for shrink animation when free spin total appears
  const prevShouldShowFreeSpinTotalRef = useRef(false);
  const winPulseAnimRef = useRef(null);

  // Get button dimensions using the same system as PopiClock - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isReelsetVariant = variant === "reelset";
  const hasFreeSpinTotal =
    freeSpinActive && displayedFreeSpinTotal > 0 && freeSpinIndex > 0;

  // Get unified win configuration (font multiplier, x, y positions) - x always center
  const winConfig = useMemo(() => {
    // Reelset variant: positioned relative to GameContainer (below reels)
    if (isReelsetVariant) {
      const baseY = symbolHeight * (Settings.SYMBOLS_VISIBLE / 2 + 0.53);
      return {
        fontMultiplier: 1.45,
        x: 0, // GameContainer is already centered
        y: hasFreeSpinTotal ? baseY + symbolHeight * 0.1 : baseY,
      };
    }

    // Toolbar variant: positioned within the toolbar
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.3;
        y = -buttonConfig.height * 4;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.5;
        y = toolbar.height / 2;
      } else {
        fontMultiplier = 1.35;
        y = toolbar.height / 2;
      }
    } else {
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, x: toolbar.width / 2, y };
  }, [
    device,
    orientation,
    isReelsetVariant,
    symbolHeight,
    toolbar?.width,
    toolbar?.height,
    buttonConfig.height,
    hasFreeSpinTotal,
  ]);

  // Calculate font sizes - memoized
  const baseFontSize = 36;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * winConfig.fontMultiplier;
    return Math.floor(responsiveFontSize * uiSymbolScale);
  }, [winConfig.fontMultiplier, uiSymbolScale]);

  // Line spacing for dual-line mode (matches FreeRoundsWinDisplay)
  const lineSpacing = useMemo(() => Math.round(FONT_SIZE * 0.6), [FONT_SIZE]);

  // Background height — proportional to toolbar or symbol
  // Expand when free spin total is showing (dual-line text needs more height)
  // Only expands on mobile portrait (reelset variant) — toolbar variant keeps constant height
  const bgHeight = useMemo(() => {
    if (isReelsetVariant) {
      return symbolHeight * (hasFreeSpinTotal ? 0.75 : 0.55);
    }
    return (toolbar?.height ?? 50) * 0.75;
  }, [isReelsetVariant, symbolHeight, toolbar?.height, hasFreeSpinTotal]);

  // Background draw — full width on mobile portrait, hook-provided width otherwise
  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;
  const bgDraw = useMemo(() => {
    const isTransparent =
      FEATURES_CONFIG.TOOLBAR.REELSET_WIN_DISPLAY?.TOOLBAR_COLOR ===
      "transparent";
    let w;
    if (isReelsetVariant) {
      // Reelset variant: full screen width (GameContainer is centered at dimensions.width/2)
      w = dimensions.width;
    } else if (isMobilePortrait) {
      w = toolbar?.width ?? 200;
    } else {
      w = toolbar?.layout?.winDisplay?.width ?? 200;
    }
    return (g) => {
      g.clear();
      if (isTransparent) return;
      g.beginFill(0x090f41, isReelsetVariant ? 0.35 : 0.55);
      g.drawRect(-w / 2, -bgHeight / 2, w, bgHeight);
      g.endFill();
    };
  }, [
    isReelsetVariant,
    isMobilePortrait,
    dimensions.width,
    toolbar?.width,
    toolbar?.layout?.winDisplay?.width,
    bgHeight,
  ]);

  // Alpha based on variant + device: reelset visible only on mobile portrait, toolbar visible on all others
  const containerAlpha = useMemo(() => {
    if (isReelsetVariant) {
      return isMobilePortrait ? 1 : 0.001;
    }
    // toolbar variant
    return isMobilePortrait ? 0.001 : 1;
  }, [isReelsetVariant, isMobilePortrait]);

  // Reset component state when new spin begins (base game or free spin)
  useEffect(() => {
    if ((isSpinning && !holdAndWinActive) || freeSpinSpinning) {
      dispatch(setCounterAnimationStarted(false));
      dispatch(setCounterAnimationFinished(false));
      dispatch(setHoldAndWinOutroCounterFinished(false));
      prevShouldShowWinRef.current = false;
    }
  }, [isSpinning, holdAndWinActive, freeSpinSpinning, dispatch]);

  // On completion reload: the outro counter already finished before the reload.
  // Immediately restore the dual-line state so both lines show on mount.
  useEffect(() => {
    if (isHoldAndWinCompletionReload && triggerRoundWin > 0) {
      dispatch(setHoldAndWinOutroCounterFinished(true));
      wasHoldAndWinTriggerWithWinRef.current = true;
    }
    // Only run once when the component first sees these values on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHoldAndWinCompletionReload, triggerRoundWin]);

  // Cleanup slide animation on unmount
  useEffect(() => {
    return () => {
      if (slideAnimationRef.current) {
        slideAnimationRef.current.pause();
        slideAnimationRef.current = null;
      }
    };
  }, []);

  // Check if we have a Hold and Win win to display (after feature completes, non-trigger-with-win path)
  // Only for the case where there was NO regular win in the trigger round.
  // Also exclude the latched dual-line case (wasHoldAndWinTriggerWithWinRef) so the
  // single-line path never fires while we're still showing the dual-line display.
  const hasHoldAndWinWin = useMemo(
    () =>
      holdAndWinCurrentRound?.win > 0 &&
      !isHoldAndWinTriggerRound &&
      !isHoldAndWinTriggerWithWin &&
      !wasHoldAndWinTriggerWithWinRef.current,
    [
      holdAndWinCurrentRound?.win,
      isHoldAndWinTriggerRound,
      isHoldAndWinTriggerWithWin,
    ],
  );

  // Regular single-line shouldShowWin:
  // - Regular win (no H&W active, no H&W trigger)
  // - H&W trigger round before H&W starts (shows Win: 330 briefly)
  // - H&W win (no trigger win): show as soon as outro counter finishes, same timing as dual-line
  // - H&W win fallback: after feature + transition fully complete (existing path)
  // CRITICAL: !holdAndWinActive prevents the first branch from firing during H&W respins
  // CRITICAL: !wasHoldAndWinTriggerWithWinRef.current prevents the first branch from firing
  //           after H&W ends (latch still active) — dual-line handles display in that case
  const shouldShowWin = useMemo(
    () =>
      // Win celebration (Big/Mega/SuperMega): show as soon as counter finishes
      (winAnimationCounterFinished && currentRound?.win > 0 && !isSpinning) ||
      // H&W trigger round with animated win: keep single-line visible until dual-line takes over
      (wasHoldAndWinTriggerRoundRef.current &&
        !holdAndWinActive &&
        !isSpinning) ||
      // Regular win (no animated win, no H&W active)
      (counterAnimationStarted &&
        currentRound?.win > 0 &&
        !isSpinning &&
        !criticalAnimationPlaying &&
        !holdAndWinActive &&
        !wasHoldAndWinTriggerWithWinRef.current &&
        (!isAnimatedWin || !winAnimationsPlaying)) ||
      // Regular H&W (no trigger win): show Win as soon as outro counter finishes
      (hasHoldAndWinWin && holdAndWinOutroCounterFinished && !isSpinning) ||
      // Fallback: show after H&W fully exits (transition complete)
      (hasHoldAndWinWin &&
        !holdAndWinActive &&
        !showHoldAndWinIntro &&
        !isSpinning &&
        !criticalAnimationPlaying &&
        (!isAnimatedWin || !winAnimationsPlaying)) ||
      // Free spin win: show when counter animation started during free spins
      (freeSpinActive &&
        counterAnimationStarted &&
        currentRound?.win > 0 &&
        !freeSpinSpinning &&
        !criticalAnimationPlaying &&
        (!isAnimatedWin || !winAnimationsPlaying)),
    [
      counterAnimationStarted,
      currentRound?.win,
      isSpinning,
      criticalAnimationPlaying,
      hasHoldAndWinWin,
      holdAndWinActive,
      holdAndWinOutroCounterFinished,
      winAnimationCounterFinished,
      showHoldAndWinIntro,
      isAnimatedWin,
      winAnimationsPlaying,
      freeSpinActive,
      freeSpinSpinning,
    ],
  );

  // Dual-line mode: show Total Win during H&W active when trigger round had a regular win.
  // Also stays true via the latch after H&W ends (during exit transition and in base game)
  // until the next spin clears wasHoldAndWinTriggerWithWinRef.
  // triggerRoundWin > 0 guard prevents a brief "Total Win: 0" flash after the latch resets
  // but before Redux clears triggerRoundWin on the next spin.
  const shouldShowDualLine = useMemo(
    () =>
      triggerRoundWin > 0 &&
      (isHoldAndWinTriggerWithWin || wasHoldAndWinTriggerWithWinRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isHoldAndWinTriggerWithWin, triggerRoundWin],
  );

  // In dual-line mode, Total Win = trigger round win + H&W win (after counter finishes).
  // Uses shouldShowDualLine (which includes the latch) so the value persists after H&W ends.
  const totalWinAmount = useMemo(() => {
    if (!shouldShowDualLine) return 0;
    const hnwWin = holdAndWinOutroCounterFinished
      ? holdAndWinCurrentRound?.win || 0
      : 0;
    return triggerRoundWin + hnwWin;
  }, [
    shouldShowDualLine,
    holdAndWinOutroCounterFinished,
    holdAndWinCurrentRound?.win,
    triggerRoundWin,
  ]);

  // Use Hold and Win win if available (non-trigger-with-win path), otherwise regular win
  const displayWin = useMemo(
    () => (hasHoldAndWinWin ? holdAndWinCurrentRound.win : currentRound.win),
    [hasHoldAndWinWin, holdAndWinCurrentRound?.win, currentRound?.win],
  );

  // Balance update for regular single-line wins (non H&W trigger with win path)
  useEffect(() => {
    if (shouldShowWin && !prevShouldShowWinRef.current) {
      const isFreeRoundsActive =
        freeRoundsStatus === "active" || freeRoundsStatus === "played";
      const shouldSkipBalanceUpdate =
        freeSpinActive || // Skip - balance handled at end of free spin session
        isHoldAndWinCompletionReload ||
        isFreeRoundsActive ||
        isHoldAndWinTriggerRound ||
        wasHoldAndWinTriggerRoundRef.current || // Skip - trigger round latch, balance handled by H&W flow
        isHoldAndWinTriggerWithWin || // Skip - balance handled by scene transition exit
        (hasHoldAndWinWin && holdAndWinOutroCounterFinished); // Skip - balance handled by refreshGameData after transition

      if (!shouldSkipBalanceUpdate) {
        dispatch(updateDisplayedBalance());
      }
    }

    prevShouldShowWinRef.current = shouldShowWin;
  }, [
    shouldShowWin,
    dispatch,
    freeSpinActive,
    isHoldAndWinCompletionReload,
    freeRoundsStatus,
    isHoldAndWinTriggerRound,
    isHoldAndWinTriggerWithWin,
    counterAnimationStarted,
    hasHoldAndWinWin,
    holdAndWinOutroCounterFinished,
    winAnimationCounterFinished,
    holdAndWinActive,
    criticalAnimationPlaying,
    winAnimationsPlaying,
    isAnimatedWin,
  ]);

  // Pulse animation when win text first appears (single-line mode)
  useEffect(() => {
    // Check if total win is appearing for the first time this render
    const isFreeSpinTotalFirstAppearing =
      freeSpinActive &&
      displayedFreeSpinTotal > 0 &&
      freeSpinIndex > 0 &&
      !prevShouldShowFreeSpinTotalRef.current;

    if (
      shouldShowWin &&
      !prevShowWinForPulseRef.current &&
      winTextContainerRef.current &&
      !isFreeSpinTotalFirstAppearing // skip pulse only on first total win appearance
    ) {
      if (winPulseAnimRef.current) winPulseAnimRef.current.pause();
      const origX = winTextContainerRef.current.scale.x;
      const origY = winTextContainerRef.current.scale.y;
      winPulseAnimRef.current = anime({
        targets: winTextContainerRef.current.scale,
        x: origX * (freeSpinActive ? 1.1 : 1.15),
        y: origY * (freeSpinActive ? 1.1 : 1.15),
        duration: 80,
        easing: "easeOutQuad",
        complete: () => {
          winPulseAnimRef.current = anime({
            targets: winTextContainerRef.current.scale,
            x: origX,
            y: origY,
            duration: 180,
            easing: "easeOutQuad",
          });
        },
      });
    }
    prevShowWinForPulseRef.current = shouldShowWin;
  }, [shouldShowWin, freeSpinActive, displayedFreeSpinTotal, freeSpinIndex]);

  // Sync displayed free spin total ONLY when win text becomes visible
  useEffect(() => {
    if (
      freeSpinActive &&
      shouldShowWin &&
      !prevShowWinForSyncRef.current &&
      freeSpinTotal > 0
    ) {
      // Win just appeared — latch the current total
      displayedFreeSpinTotalRef.current = freeSpinTotal;
      setDisplayedFreeSpinTotal(freeSpinTotal);
    }
    // Reset when free spins end
    if (!freeSpinActive && displayedFreeSpinTotalRef.current > 0) {
      displayedFreeSpinTotalRef.current = 0;
      setDisplayedFreeSpinTotal(0);
    }
    prevShowWinForSyncRef.current = shouldShowWin;
  }, [freeSpinActive, shouldShowWin, freeSpinTotal]);

  // Pulse animation when displayed free spin total updates (synced with win text)
  useEffect(() => {
    if (
      freeSpinActive &&
      displayedFreeSpinTotal > 0 &&
      displayedFreeSpinTotal !== prevFreeSpinTotalRef.current &&
      freeSpinTotalContainerRef.current
    ) {
      if (freeSpinTotalPulseRef.current) freeSpinTotalPulseRef.current.pause();
      const origX = freeSpinTotalContainerRef.current.scale.x;
      const origY = freeSpinTotalContainerRef.current.scale.y;
      freeSpinTotalPulseRef.current = anime({
        targets: freeSpinTotalContainerRef.current.scale,
        x: origX * 1.1,
        y: origY * 1.1,
        duration: 80,
        easing: "easeOutQuad",
        complete: () => {
          freeSpinTotalPulseRef.current = anime({
            targets: freeSpinTotalContainerRef.current.scale,
            x: origX,
            y: origY,
            duration: 180,
            easing: "easeOutQuad",
          });
        },
      });
    }
    prevFreeSpinTotalRef.current = displayedFreeSpinTotal;
  }, [freeSpinActive, displayedFreeSpinTotal]);

  // Pulse animation when dual-line mode first appears
  useEffect(() => {
    if (
      shouldShowDualLine &&
      !prevShouldShowDualLineRef.current &&
      winTextContainerRef.current
    ) {
      if (winPulseAnimRef.current) winPulseAnimRef.current.pause();
      const origX = winTextContainerRef.current.scale.x;
      const origY = winTextContainerRef.current.scale.y;
      winPulseAnimRef.current = anime({
        targets: winTextContainerRef.current.scale,
        x: origX * 1.15,
        y: origY * 1.15,
        duration: 80,
        easing: "easeOutQuad",
        complete: () => {
          winPulseAnimRef.current = anime({
            targets: winTextContainerRef.current.scale,
            x: origX,
            y: origY,
            duration: 180,
            easing: "easeOutQuad",
          });
        },
      });
    }
    prevShouldShowDualLineRef.current = shouldShowDualLine;
  }, [shouldShowDualLine]);

  // Balance for the H&W trigger-with-win path is handled by SceneTransitionOverlay,
  // which dispatches refreshGameData() on exit — that API response sets displayedBalance
  // directly from the real server balance. No manual dispatch needed here.

  // Animate Total Win container position when H&W win sub-line appears (dual-line mode)
  useEffect(() => {
    if (!totalWinContainerRef.current || !shouldShowDualLine) return;

    if (slideAnimationRef.current) {
      slideAnimationRef.current.pause();
      slideAnimationRef.current = null;
    }

    if (holdAndWinOutroCounterFinished) {
      // Sub-line appeared → slide Total Win UP
      slideAnimationRef.current = anime({
        targets: totalWinContainerRef.current,
        y: -lineSpacing,
        duration: 250,
        easing: "easeOutCubic",
        update: (anim) => {
          if (!totalWinContainerRef.current) anim.pause();
        },
      });
    } else {
      // No sub-line → Total Win centered
      totalWinContainerRef.current.y = 0;
    }

    return () => {
      if (slideAnimationRef.current) {
        slideAnimationRef.current.pause();
        slideAnimationRef.current = null;
      }
    };
  }, [holdAndWinOutroCounterFinished, shouldShowDualLine, lineSpacing]);

  // Text style objects - memoized
  const winLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(FONT_SIZE * 0.7),
        fontWeight: "700",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  const winStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  const currencyStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(FONT_SIZE * 0.7),
        fontWeight: "700",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  // Bottom-align currency text with the larger value text
  const currencyYOffset = useMemo(() => {
    const valueMetrics = PIXI.TextMetrics.measureText("0", winStyle);
    const currMetrics = PIXI.TextMetrics.measureText("0", currencyStyle);
    return Math.round((valueMetrics.height - currMetrics.height) * 0.3);
  }, [winStyle, currencyStyle]);

  // Format text values
  const winText = useMemo(() => formatToCustomLocale(displayWin), [displayWin]);
  const totalWinText = useMemo(
    () => formatToCustomLocale(totalWinAmount),
    [totalWinAmount],
  );
  const hnwWinText = useMemo(
    () => formatToCustomLocale(holdAndWinCurrentRound?.win || 0),
    [holdAndWinCurrentRound?.win],
  );
  const freeSpinTotalText = useMemo(
    () => formatToCustomLocale(displayedFreeSpinTotal),
    [displayedFreeSpinTotal],
  );
  const currencyText = useMemo(() => ` ${currency}`, [currency]);
  const winLabelText = `${t("win", { ns: "common" }).toUpperCase()} `;
  const totalWinLabelText = `${t("total_win", { ns: "common" }).toUpperCase()} `;
  const freeSpinTotalLabelText = `${t("total_win", { ns: "common" }).toUpperCase()} `;

  // Whether to show the free spin total win line
  // Skip trigger round (index 0) — same as FreeSpinCounter
  const shouldShowFreeSpinTotal = useMemo(
    () => freeSpinActive && displayedFreeSpinTotal > 0 && freeSpinIndex > 0,
    [freeSpinActive, displayedFreeSpinTotal, freeSpinIndex],
  );

  // Free spin total line metrics
  // (using freeSpinTotalMetrics below with the correct styles instead)

  // Free spin total uses 0.9x styles (slightly smaller than full, but still prominent)
  const freeSpinTotalLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(FONT_SIZE * 0.9 * 0.7),
        fontWeight: "700",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );
  const freeSpinTotalValueStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(FONT_SIZE * 0.9),
        fontWeight: "700",
        fill: "#ffbf11",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );
  const freeSpinTotalCurrencyStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(FONT_SIZE * 0.9 * 0.7),
        fontWeight: "700",
        fill: "#ffffff",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  // Re-measure free spin total with its own styles
  const freeSpinTotalMetrics = useMemo(() => {
    if (!shouldShowFreeSpinTotal) return null;
    const labelMetrics = PIXI.TextMetrics.measureText(
      freeSpinTotalLabelText,
      freeSpinTotalLabelStyle,
    );
    const valueMetrics = PIXI.TextMetrics.measureText(
      freeSpinTotalText,
      freeSpinTotalValueStyle,
    );
    const currMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      freeSpinTotalCurrencyStyle,
    );
    const lineWidth =
      labelMetrics.width + valueMetrics.width + currMetrics.width;
    return { labelMetrics, valueMetrics, currMetrics, lineWidth };
  }, [
    shouldShowFreeSpinTotal,
    freeSpinTotalLabelText,
    freeSpinTotalText,
    currencyText,
    freeSpinTotalLabelStyle,
    freeSpinTotalValueStyle,
    freeSpinTotalCurrencyStyle,
  ]);

  // Text metrics for single-line mode - memoized
  const singleLineMetrics = useMemo(() => {
    const winLabelMetrics = PIXI.TextMetrics.measureText(
      winLabelText,
      winLabelStyle,
    );
    const winTextMetrics = PIXI.TextMetrics.measureText(winText, winStyle);
    const currencyTextMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      currencyStyle,
    );
    const totalTextWidth =
      winLabelMetrics.width + winTextMetrics.width + currencyTextMetrics.width;

    return {
      winLabelMetrics,
      winTextMetrics,
      currencyTextMetrics,
      totalTextWidth,
    };
  }, [
    winLabelText,
    winText,
    currencyText,
    winLabelStyle,
    winStyle,
    currencyStyle,
  ]);

  // Text metrics for dual-line mode - memoized
  const dualLineMetrics = useMemo(() => {
    const totalWinLabelMetrics = PIXI.TextMetrics.measureText(
      totalWinLabelText,
      winLabelStyle,
    );
    const totalWinTextMetrics = PIXI.TextMetrics.measureText(
      totalWinText,
      winStyle,
    );
    const currencyTextMetrics = PIXI.TextMetrics.measureText(
      currencyText,
      currencyStyle,
    );
    const hnwWinTextMetrics = PIXI.TextMetrics.measureText(
      hnwWinText,
      winStyle,
    );
    const winLabelMetrics = PIXI.TextMetrics.measureText(
      winLabelText,
      winLabelStyle,
    );

    const totalWinLineWidth =
      totalWinLabelMetrics.width +
      totalWinTextMetrics.width +
      currencyTextMetrics.width;
    const hnwWinLineWidth =
      winLabelMetrics.width +
      hnwWinTextMetrics.width +
      currencyTextMetrics.width;

    return {
      totalWinLabelMetrics,
      totalWinTextMetrics,
      currencyTextMetrics,
      hnwWinTextMetrics,
      winLabelMetrics,
      totalWinLineWidth,
      hnwWinLineWidth,
    };
  }, [
    totalWinLabelText,
    totalWinText,
    hnwWinText,
    currencyText,
    winLabelText,
    winLabelStyle,
    winStyle,
    currencyStyle,
  ]);

  // Auto-scale text to fit within win display background
  const bgWidth = isReelsetVariant
    ? dimensions.width
    : isMobilePortrait
      ? (toolbar?.width ?? 200)
      : (toolbar?.layout?.winDisplay?.width ?? 200);
  const bgPadding = 10; // padding from each side of the background
  const maxTextWidth = bgWidth - bgPadding * 2;

  // Calculated positions for free spin dual-line layout
  // Mobile portrait (reelset): horizontal — Total Win left, Win right
  // Other devices: vertical — Total Win top, Win/Good Luck bottom
  const freeSpinLayout = useMemo(() => {
    if (!shouldShowFreeSpinTotal) return null;

    // Vertical stacked layout — Total Win on top, Win/Good Luck below
    const shrinkScale = 0.8;
    const totalWinFontSize = Math.floor(FONT_SIZE * 0.9);
    const winFontSize = Math.floor(FONT_SIZE * 0.85);
    const totalWinH = totalWinFontSize * shrinkScale;
    const winH = winFontSize * shrinkScale;
    const totalH = totalWinH + winH;
    const availableH = bgHeight;
    const padding = (availableH - totalH) / 3;
    const bottomPadding = padding * 0.7;
    const totalWinY = -availableH / 2 + padding + totalWinH / 2;
    const winY = availableH / 2 - bottomPadding - winH / 2;
    const goodLuckY = winY;
    return { totalWinX: 0, winX: 0, totalWinY, winY, goodLuckY };
  }, [shouldShowFreeSpinTotal, FONT_SIZE, bgHeight]);

  const textScale = useMemo(() => {
    // Find the widest line of text
    const widestLine = shouldShowDualLine
      ? Math.max(
          singleLineMetrics.totalTextWidth,
          dualLineMetrics.totalWinLineWidth,
          dualLineMetrics.hnwWinLineWidth,
        )
      : singleLineMetrics.totalTextWidth;

    if (widestLine > maxTextWidth && maxTextWidth > 0) {
      return maxTextWidth / widestLine;
    }
    return 1;
  }, [shouldShowDualLine, singleLineMetrics, dualLineMetrics, maxTextWidth]);

  // Apply 0.8x shrink factor when free spin total will be/is showing
  // Uses freeSpinTotal (Redux, immediate) instead of displayedFreeSpinTotal (buffered, 1-frame delay)
  // so the container is already shrunk before the win text first renders
  // Apply 0.8x shrink when free spin total is showing (makes room for dual-line text)
  const shouldShrinkForFreeSpinTotal =
    freeSpinActive && freeSpinTotal > 0 && freeSpinIndex > 0;
  const finalTextScale = useMemo(
    () => (shouldShrinkForFreeSpinTotal ? textScale * 0.8 : textScale),
    [textScale, shouldShrinkForFreeSpinTotal],
  );

  // Track shouldShowFreeSpinTotal transitions
  useEffect(() => {
    prevShouldShowFreeSpinTotalRef.current = shouldShowFreeSpinTotal;
  }, [shouldShowFreeSpinTotal]);

  // Smooth expand/collapse animation when hasFreeSpinTotal transitions
  // Only applies to reelset variant (mobile portrait) — toolbar keeps constant height
  useEffect(() => {
    if (!outerContainerRef.current) return;

    // Toolbar variant: no expand animation, just set Y
    if (!isReelsetVariant) {
      outerContainerRef.current.y = winConfig.y;
      return;
    }

    // First render — just set position immediately
    if (prevHasFreeSpinTotalRef.current === hasFreeSpinTotal) {
      outerContainerRef.current.y = winConfig.y;
      return;
    }

    // Transition — animate Y position
    if (expandAnimRef.current) expandAnimRef.current.pause();
    expandAnimRef.current = anime({
      targets: outerContainerRef.current,
      y: winConfig.y,
      duration: 200,
      easing: "easeOutQuad",
    });

    // Animate background scaleY
    if (bgGraphicsRef.current) {
      const normalHeight = symbolHeight * 0.55;
      const expandedHeight = symbolHeight * 0.75;
      // Expanding: start from normal/expanded ratio (small → 1)
      // Collapsing: start from expanded/normal ratio (big → 1)
      const fromScale = hasFreeSpinTotal
        ? normalHeight / expandedHeight
        : expandedHeight / normalHeight;
      bgGraphicsRef.current.scale.y = fromScale;
      anime({
        targets: bgGraphicsRef.current.scale,
        y: 1,
        duration: 200,
        easing: "easeOutQuad",
      });
    }

    prevHasFreeSpinTotalRef.current = hasFreeSpinTotal;
  }, [hasFreeSpinTotal, winConfig.y, bgHeight, isReelsetVariant, symbolHeight]);

  // Hide entirely during free rounds (FreeRoundsWinDisplay handles everything including bg)
  const isFreeRoundsHidden =
    FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
    (freeRoundsStatus !== null ||
      freeRoundsEnded ||
      freeRoundsCompletionPending);

  // Screen reader announcement — dynamic based on what's currently showing
  const accessibleAnnouncement = useMemo(() => {
    const parts = [];
    if (displayWin > 0) {
      parts.push(
        `${t("win", { ns: "common" })}: ${formatToCustomLocale(displayWin)} ${currency}`,
      );
    }
    if (shouldShowDualLine && totalWinAmount > 0) {
      parts.push(
        `${t("total_win", { ns: "common" })}: ${formatToCustomLocale(totalWinAmount)} ${currency}`,
      );
    }
    if (shouldShowFreeSpinTotal && displayedFreeSpinTotal > 0) {
      parts.push(
        `${t("total_win", { ns: "common" })}: ${formatToCustomLocale(displayedFreeSpinTotal)} ${currency}`,
      );
    }
    return parts.join(". ") || t("win", { ns: "common" });
  }, [
    displayWin,
    shouldShowDualLine,
    totalWinAmount,
    shouldShowFreeSpinTotal,
    displayedFreeSpinTotal,
    currency,
    t,
  ]);

  if (isFreeRoundsHidden) {
    return null;
  }

  // Destructure metrics
  const {
    winLabelMetrics: singleWinLabelMetrics,
    winTextMetrics: singleWinTextMetrics,
    currencyTextMetrics: singleCurrencyTextMetrics,
    totalTextWidth,
  } = singleLineMetrics;

  const {
    totalWinLabelMetrics,
    totalWinTextMetrics,
    currencyTextMetrics: dualCurrencyTextMetrics,
    hnwWinTextMetrics,
    winLabelMetrics: dualWinLabelMetrics,
    totalWinLineWidth,
    hnwWinLineWidth,
  } = dualLineMetrics;

  // Always render container + background; text content is conditional

  return (
    <Container
      ref={outerContainerRef}
      name={isReelsetVariant ? "reelset_win_display" : "secondary_win_display"}
      x={winConfig.x}
      y={winConfig.y}
      alpha={containerAlpha}
      zIndex={
        isReelsetVariant
          ? FEATURES_CONFIG.TOOLBAR.REELSET_WIN_DISPLAY.BACKGROUND_MODE ===
            "front"
            ? 5
            : -1
          : 4
      }
      accessible={true}
      accessibleTitle={accessibleAnnouncement}
    >
      {/* Background — always visible */}
      <Graphics ref={bgGraphicsRef} draw={bgDraw} />

      {/* Text content — auto-scaled to fit background */}
      <Container ref={winTextContainerRef} scale={finalTextScale}>
        {shouldShowDualLine && (
          <>
            <Container ref={totalWinContainerRef} name="total_win_line" y={0}>
              <Text
                key="total_win_label"
                text={totalWinLabelText}
                style={winLabelStyle}
                anchor={0.5}
                resolution={2}
                x={Math.round(
                  -totalWinLineWidth / 2 + totalWinLabelMetrics.width / 2,
                )}
                y={currencyYOffset}
              />
              <Text
                key={`total_win_amount_${totalWinText}`}
                text={totalWinText}
                style={winStyle}
                anchor={0.5}
                resolution={2}
                x={Math.round(
                  -totalWinLineWidth / 2 +
                    totalWinLabelMetrics.width +
                    totalWinTextMetrics.width / 2,
                )}
              />
              <Text
                key="total_win_currency"
                text={currencyText}
                style={currencyStyle}
                anchor={0.5}
                resolution={2}
                y={currencyYOffset}
                x={Math.round(
                  totalWinLineWidth / 2 - dualCurrencyTextMetrics.width / 2,
                )}
              />
            </Container>

            {holdAndWinOutroCounterFinished && (
              <Container name="hnw_win_line" y={lineSpacing}>
                <Text
                  key="hnw_win_label"
                  text={winLabelText}
                  style={winLabelStyle}
                  anchor={0.5}
                  resolution={2}
                  x={Math.round(
                    -hnwWinLineWidth / 2 + dualWinLabelMetrics.width / 2,
                  )}
                  y={currencyYOffset}
                />
                <Text
                  key={`hnw_win_amount_${hnwWinText}`}
                  text={hnwWinText}
                  style={winStyle}
                  anchor={0.5}
                  resolution={2}
                  x={Math.round(
                    -hnwWinLineWidth / 2 +
                      dualWinLabelMetrics.width +
                      hnwWinTextMetrics.width / 2,
                  )}
                />
                <Text
                  key="hnw_win_currency"
                  text={currencyText}
                  style={currencyStyle}
                  anchor={0.5}
                  resolution={2}
                  y={currencyYOffset}
                  x={Math.round(
                    hnwWinLineWidth / 2 - dualCurrencyTextMetrics.width / 2,
                  )}
                />
              </Container>
            )}
          </>
        )}

        {/* Single-line mode: regular win or H&W completion win */}
        {!shouldShowDualLine && shouldShowWin && (
          <Container
            x={freeSpinLayout ? freeSpinLayout.winX : 0}
            y={freeSpinLayout ? freeSpinLayout.winY : 0}
          >
            <Text
              key={winLabelText}
              text={winLabelText}
              style={winLabelStyle}
              anchor={0.5}
              resolution={2}
              x={-totalTextWidth / 2 + singleWinLabelMetrics.width / 2}
              y={currencyYOffset}
            />
            <Text
              key={winText}
              text={winText}
              style={winStyle}
              anchor={0.5}
              resolution={2}
              x={
                -totalTextWidth / 2 +
                singleWinLabelMetrics.width +
                singleWinTextMetrics.width / 2
              }
            />
            <Text
              key={currencyText}
              text={currencyText}
              style={currencyStyle}
              anchor={0.5}
              resolution={2}
              y={currencyYOffset}
              x={totalTextWidth / 2 - singleCurrencyTextMetrics.width / 2}
            />
          </Container>
        )}
      </Container>

      {/* Good Luck text — shown when no win is displayed (not scaled) */}
      {!shouldShowDualLine && !shouldShowWin && (
        <Text
          text={t("good_luck").toUpperCase()}
          style={
            new TextStyle({
              fontFamily: FONT_CONFIG.PRIMARY,
              fontSize: shouldShowFreeSpinTotal
                ? Math.floor(FONT_SIZE * 0.85 * 0.8)
                : FONT_SIZE * 0.85,
              fontWeight: "600",
              fill: "#ffffff",
              align: "center",
            })
          }
          anchor={0.5}
          resolution={2}
          x={freeSpinLayout ? freeSpinLayout.winX : 0}
          y={freeSpinLayout ? freeSpinLayout.goodLuckY : 0}
        />
      )}

      {/* Free Spin Total Win — shown above current text during free spins */}
      {shouldShowFreeSpinTotal && freeSpinTotalMetrics && (
        <Container
          ref={freeSpinTotalContainerRef}
          name="free_spin_total_win"
          x={freeSpinLayout ? freeSpinLayout.totalWinX : 0}
          y={freeSpinLayout ? freeSpinLayout.totalWinY : 0}
        >
          <Text
            text={freeSpinTotalLabelText}
            style={freeSpinTotalLabelStyle}
            anchor={0.5}
            resolution={2}
            y={currencyYOffset}
            x={Math.round(
              -freeSpinTotalMetrics.lineWidth / 2 +
                freeSpinTotalMetrics.labelMetrics.width / 2,
            )}
          />
          <Text
            key={`fs_total_${freeSpinTotalText}`}
            text={freeSpinTotalText}
            style={freeSpinTotalValueStyle}
            anchor={0.5}
            resolution={2}
            x={Math.round(
              -freeSpinTotalMetrics.lineWidth / 2 +
                freeSpinTotalMetrics.labelMetrics.width +
                freeSpinTotalMetrics.valueMetrics.width / 2,
            )}
          />
          <Text
            text={currencyText}
            style={freeSpinTotalCurrencyStyle}
            anchor={0.5}
            resolution={2}
            y={currencyYOffset}
            x={Math.round(
              freeSpinTotalMetrics.lineWidth / 2 -
                freeSpinTotalMetrics.currMetrics.width / 2,
            )}
          />
        </Container>
      )}
    </Container>
  );
};

export default WinDisplay;
